import React, { useState, useEffect, useRef } from "react";
import { tWithKeyAttr as t } from "../../../../i18nHelper";
import i18n from "../../../../i18n";
import styles from "./EditAppointmentModal.module.css";
import { IAppointment } from "../../types/appointments.types";
import {
  useValidateAppointmentChangeMutation,
  usePatchAppointmentMutation,
  useUpdateAppointmentDeviceMutation,
} from "../../../../services/api/utils.api";
import { useAppointmentsContext } from "../../context/AppointmentsContext";
import { useLazyGetCompanyDevicesQuery } from "../../../../services/api/user.api";
import { AppointmentDevice, StaffDevice } from "../../../../services/types/device.types";
import { getDeviceSummaryLabel } from "../../../../utils/deviceConfiguration";
import DeviceConfigurationModal from "../DeviceConfigurationModal/DeviceConfigurationModal";
import { timeToMinutes } from "../../../../utils/utils";
import { calculateEndTime } from "../../utils/timeUtils";
import SeriesManager from "../SeriesManager/SeriesManager";

/** Devices are operated by technicians (roleId 8); the machines belong to the company. */

interface IEditAppointmentModalProps {
  appointment: IAppointment;
  onClose: () => void;
  onSuccess: () => void;
  isEmbedded?: boolean;
}

interface IConflict {
  type: string;
  message: string;
  severity: 'warning' | 'info';
}

interface ITherapistAvailability {
  dayId: number;
  startTime: string;
  endTime: string;
}

/**
 * Edit Appointment Modal Component
 *
 * Allows editing appointment details with validation:
 * - Date and time
 * - Therapist
 * - Media/Method
 * - Notes/Comments
 * 
 * Note: Changes apply only to the specific appointment, not the entire treatment program.
 */
const EditAppointmentModal: React.FC<IEditAppointmentModalProps> = ({
  appointment,
  onClose,
  onSuccess,
  isEmbedded = false,
}) => {
  const isRtl = i18n.language === "he";

  // Form state
  const [date, setDate] = useState(appointment.date);
  const [startTime, setStartTime] = useState(appointment.time || "");
  const [endTime, setEndTime] = useState(appointment.endTime || "");
  const [therapistId, setTherapistId] = useState(appointment.therapistsId || 0);
  const [mediaId, setMediaId] = useState(appointment.mediaId || 0);
  const [comments, setComments] = useState(appointment.comments || "");
  const [saveError, setSaveError] = useState("");

  // Update state when appointment prop changes
  useEffect(() => {
    setDate(appointment.date);
    setStartTime(appointment.time || "");
    setEndTime(appointment.endTime || "");
    setTherapistId(appointment.therapistsId || 0);
    setMediaId(appointment.mediaId || 0);
    setComments(appointment.comments || "");
  }, [appointment]);

  // Edit mode states
  const [isEditingTherapist, setIsEditingTherapist] = useState(false);
  const [showTherapistDropdown, setShowTherapistDropdown] = useState(false);
  const [showMediaDropdown, setShowMediaDropdown] = useState(false);
  const therapistDropdownRef = useRef<HTMLDivElement>(null);
  const mediaDropdownRef = useRef<HTMLDivElement>(null);

  // Validation state
  const [conflicts, setConflicts] = useState<IConflict[]>([]);
  const [therapistAvailability, setTherapistAvailability] = useState<ITherapistAvailability[]>([]);
  const [isValidating, setIsValidating] = useState(false);

  // Get therapists from context (already loaded)
  const { therapistsList } = useAppointmentsContext();

  // API hooks
  const [validateChange] = useValidateAppointmentChangeMutation();
  const [patchAppointment, { isLoading: isPatchingAppointment }] = usePatchAppointmentMutation();
  const [updateAppointmentDevice] = useUpdateAppointmentDeviceMutation();
  const [getCompanyDevices, { isFetching: isFetchingDevices }] = useLazyGetCompanyDevicesQuery();

  const isOther = !appointment.usersId && !appointment.treatmentGroupId && appointment.source !== "google_calendar";

  // Media types - hardcoded for now, should come from API
  const mediaTypes = [
    { id: 1, name: t("MEDIA_CHAT") },
    { id: 2, name: t("MEDIA_VIDEO") },
    { id: 3, name: t("MEDIA_PHONE") },
    { id: 4, name: t("MEDIA_CLINIC") },
  ];

  // Validate changes when relevant fields change
  useEffect(() => {
    const validateChanges = async () => {
      // Skip validation for "Other" events (no patient attached)
      if (isOther) return;

      setIsValidating(true);
      try {
        const therapistsGuidForValidate =
          therapistId === appointment.therapistsId
            ? appointment.therapistsGuid
            : therapistsList.find((t) => t.id === therapistId)?.guid;

        const result = await validateChange({
          appointmentId: appointment.id,
          therapistsGuid: therapistsGuidForValidate,
          usersGuid: appointment.usersGuid,
          date: date || appointment.date,
          startTime: startTime || appointment.time,
          endTime: endTime || appointment.endTime,
        }).unwrap();

        setConflicts(result.conflicts || []);
        setTherapistAvailability(result.therapistAvailability || []);
      } catch (error) {
        console.error("Validation error:", error);
      } finally {
        setIsValidating(false);
      }
    };

    // Only validate if something changed
    const hasChanges = 
      date !== appointment.date ||
      startTime !== appointment.time ||
      endTime !== appointment.endTime ||
      therapistId !== appointment.therapistsId;

    if (hasChanges && !isOther) {
      validateChanges();
    } else {
      setConflicts([]);
      setTherapistAvailability([]);
    }
  }, [date, startTime, endTime, therapistId, appointment, therapistsList, validateChange, isOther]);

  const isStartBeforeEnd = timeToMinutes(startTime) < timeToMinutes(endTime);

  /**
   * A device appointment can only move within the rules it was created under: the machine has to
   * belong to whoever runs it, and the session keeps the length it was booked with. The server
   * refuses otherwise, so the screen offers only what will be accepted instead of letting the user
   * compose an edit that fails on save.
   */
  const deviceContactId = appointment.device?.deviceContactId ?? 0;
  const isDeviceAppointment = deviceContactId > 0;

  // The device the user is about to save, which starts as the one already on the appointment.
  const [currentDevice, setCurrentDevice] = useState<AppointmentDevice | null>(
    appointment.device ?? null,
  );
  const [companyDevices, setCompanyDevices] = useState<StaffDevice[]>([]);
  const [showDevicePicker, setShowDevicePicker] = useState(false);

  useEffect(() => {
    setCurrentDevice(appointment.device ?? null);
  }, [appointment]);

  const lockedSessionMinutes = currentDevice?.durationMinutes ?? null;

  const openDevicePicker = async () => {
    setSaveError("");
    try {
      // The window travels with the request so a machine already taken then is not offered.
      const devices = await getCompanyDevices({
        date,
        startTime,
        endTime,
        excludeAppointmentId: appointment.id,
      }).unwrap();

      setCompanyDevices(devices);
      setShowDevicePicker(true);
    } catch {
      setSaveError(t("SOMETHING_WENT_WRONG_TRY_AGAIN_LATER"));
    }
  };

  useEffect(() => {
    if (!lockedSessionMinutes || !startTime) return;
    setEndTime(calculateEndTime(startTime, lockedSessionMinutes));
  }, [lockedSessionMinutes, startTime]);
  const handleSave = async () => {
    setSaveError("");
    try {
      // Check what fields changed
      const hasChanges = 
        date !== appointment.date ||
        startTime !== appointment.time ||
        endTime !== appointment.endTime ||
        therapistId !== appointment.therapistsId ||
        mediaId !== appointment.mediaId ||
        comments !== (appointment.comments || "");

      if (hasChanges) {
        // Find therapist GUID if therapist changed
        let therapistsGuid: string | undefined = undefined;
        if (therapistId !== appointment.therapistsId && therapistId > 0) {
          const selectedTherapist = therapistsList.find(t => t.id === therapistId);
          therapistsGuid = selectedTherapist?.guid;
        }

        // Update only the specific appointment (not the program)
        await patchAppointment({
          id: appointment.id,
          appointmentDate: date !== appointment.date ? date : undefined,
          startTime: startTime !== appointment.time ? startTime : undefined,
          endTime: endTime !== appointment.endTime ? endTime : undefined,
          therapistsId: therapistId !== appointment.therapistsId ? therapistId : undefined,
          therapistsGuid,
          mediaId: mediaId !== appointment.mediaId ? mediaId : undefined,
          comments: comments !== (appointment.comments || "") ? comments : undefined,
        }).unwrap();
      }

      // The machine goes through its own endpoint: the montage is not an editable field, so the
      // server re-derives it from the patient's protocol and only takes the device from here.
      // Saved after the times, so availability is checked against the window being saved.
      if (currentDevice && currentDevice.deviceContactId !== appointment.device?.deviceContactId) {
        await updateAppointmentDevice({
          appointmentId: appointment.id,
          device: currentDevice,
        }).unwrap();
      }

      onSuccess();
    } catch (error) {
      // The server refuses a device edit that breaks its rules (machine not the therapist's, the
      // hour already taken, the length changed). Swallowing that left the button doing nothing.
      const err = error as { data?: { message?: string; desc?: string } };
      setSaveError(
        err?.data?.message || err?.data?.desc || t("SOMETHING_WENT_WRONG_TRY_AGAIN_LATER"),
      );
      console.error("Failed to update appointment:", error);
    }
  };

  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toISOString().split('T')[0];
  };

  const getDayName = (dayId: number): string => {
    const days = [
      t("SUNDAY"),
      t("MONDAY"),
      t("TUESDAY"),
      t("WEDNESDAY"),
      t("THURSDAY"),
      t("FRIDAY"),
      t("SATURDAY"),
    ];
    return days[dayId - 1] || "";
  };

  const currentTherapistId = therapistId || appointment.therapistsId || 0;
  const therapists = therapistsList
    .filter((t) => t.isActive || t.id === currentTherapistId)
    .map((t) => ({
      id: t.id,
      fullName: t.name,
      isActive: t.isActive,
    }));

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (therapistDropdownRef.current && !therapistDropdownRef.current.contains(event.target as Node)) {
        setShowTherapistDropdown(false);
      }
      if (mediaDropdownRef.current && !mediaDropdownRef.current.contains(event.target as Node)) {
        setShowMediaDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const editFormContent = (
    <>
      <div className={styles.modalHeader}>
        <div className={styles.modalTitle}>
          <span>✏️</span>
          <span>{t("APPOINTMENTS_EDIT_TITLE")}</span>
        </div>
        <button className={styles.closeBtn} onClick={onClose}>
          ×
        </button>
      </div>

      <div className={styles.modalBody}>

          {/* Series Manager — shown at top when appointment belongs to a repeating series */}
          {appointment.seriesId && (
            <div className={styles.formGroup}>
              <label className={styles.label}>
                <span className={styles.labelIcon}>🔁</span>
                {t("SERIES_MANAGEMENT") || "סדרת פגישות"}
              </label>
              <SeriesManager
                seriesId={appointment.seriesId}
                currentAppointmentId={appointment.id}
                onSuccess={onSuccess}
              />
            </div>
          )}

          {/* Date Field */}
          <div className={styles.formGroup}>
            <label className={styles.label}>
              <span className={styles.labelIcon}>📅</span>
              {t("APPOINTMENTS_DATE")}
            </label>
            <input
              type="date"
              className={styles.input}
              value={formatDate(date)}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>

          {/* Time Fields */}
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label className={styles.label}>
                <span className={styles.labelIcon}>⏰</span>
                {t("APPOINTMENTS_START_TIME")}
              </label>
              <input
                type="time"
                className={styles.input}
                style={(!isStartBeforeEnd) ? { borderColor: '#ef4444', backgroundColor: '#fef2f2' } : {}}
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
              />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label}>
                {t("APPOINTMENTS_END_TIME")}
              </label>
              <input
                type="time"
                className={styles.input}
                value={endTime}
                disabled={Boolean(lockedSessionMinutes)}
                onChange={(e) => setEndTime(e.target.value)}
              />
              {Boolean(lockedSessionMinutes) && (
                <span style={{ fontSize: "11px", color: "#6b7280" }}>
                  {t("DEVICE_DURATION_LOCKED_HINT")} · {lockedSessionMinutes} {t("MINUTES_SHORT")}
                </span>
              )}
            </div>
            {!isStartBeforeEnd && startTime && endTime && (
              <div style={{ fontSize: '12px', color: '#dc2626', marginTop: '-10px', marginBottom: '10px' }}>
                  {t("START_TIME_BEFORE_END_TIME_ERROR") || "שעת הסיום חייבת להיות אחרי שעת ההתחלה"}
              </div>
          )}
          </div>

          {/* The machine can be swapped for another of the company's, exactly as when the
              appointment was created — same filtering by modality and by what is free. */}
          {isDeviceAppointment && (
            <div className={styles.formGroup}>
              <label className={styles.label}>
                <span className={styles.labelIcon}>🔌</span>
                {t("DEVICE_SESSION")}
              </label>
              <div className={styles.currentValueWrapper}>
                <div className={styles.currentValue}>
                  {getDeviceSummaryLabel(currentDevice)}
                </div>
                <button
                  type="button"
                  className={styles.changeButton}
                  onClick={openDevicePicker}
                  disabled={isFetchingDevices}
                >
                  {isFetchingDevices ? t("LOADING") : t("CHANGE")}
                </button>
              </div>
            </div>
          )}

          {/* Therapist Field - Only for regular appointments. A device session is booked against
              the machine and belongs to no one member of staff, so there is nothing to pick. */}
          {!isOther && !isDeviceAppointment && (
            <div className={styles.formGroup} ref={therapistDropdownRef}>
              <label className={styles.label}>
                <span className={styles.labelIcon}>👨‍⚕️</span>
                {t("APPOINTMENTS_THERAPIST")}
              </label>
              {!isEditingTherapist ? (
                <div className={styles.currentValueWrapper}>
                  <div className={styles.currentValue}>
                    👨‍⚕️ {appointment.therapistName || t("NO_THERAPIST")}
                  </div>
                  <button
                    type="button"
                    className={styles.changeButton}
                    onClick={() => {
                      setIsEditingTherapist(true);
                      setShowTherapistDropdown(true);
                    }}
                  >
                    {t("CHANGE")}
                  </button>
                </div>
              ) : (
                <div className={styles.selectWrapper}>
                  <div className={styles.customSelect}>
                    <div
                      className={`${styles.customSelectTrigger} ${showTherapistDropdown ? styles.customSelectActive : ''}`}
                      onClick={() => setShowTherapistDropdown(!showTherapistDropdown)}
                    >
                      <span className={styles.customSelectValue}>
                        👨‍⚕️ {therapists.find(t => t.id === therapistId)?.fullName || t("SELECT_THERAPIST")}
                      </span>
                      <span className={styles.customSelectArrow}>▼</span>
                    </div>
                    {showTherapistDropdown && (
                      <div className={styles.customSelectDropdown}>
                        <div className={styles.dropdownOptions}>
                          {therapists.length === 0 ? (
                            <div className={styles.dropdownOption}>
                              <span className={styles.optionText}>{t("LOADING")}...</span>
                            </div>
                          ) : (
                            therapists.map((th: any) => (
                              <div
                                key={th.id}
                                className={`${styles.dropdownOption} ${therapistId === th.id ? styles.dropdownOptionSelected : ''}`}
                                onClick={() => {
                                  setTherapistId(th.id);
                                  setShowTherapistDropdown(false);
                                }}
                              >
                                <span className={styles.optionText}>👨‍⚕️ {th.fullName}</span>
                                {therapistId === th.id && <span className={styles.checkmark}>✓</span>}
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    className={styles.cancelButton}
                    onClick={() => {
                      setIsEditingTherapist(false);
                      setShowTherapistDropdown(false);
                      setTherapistId(appointment.therapistsId || 0);
                    }}
                  >
                    {t("CANCEL")}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Media Field - Only for regular appointments */}
          {appointment.usersId || appointment.treatmentGroupId ? (
            <div className={styles.formGroup} ref={mediaDropdownRef}>
              <label className={styles.label}>
                <span className={styles.labelIcon}>📍</span>
                {t("APPOINTMENTS_MEDIA")}
              </label>
              <div className={styles.customSelect}>
                <div
                  className={`${styles.customSelectTrigger} ${showMediaDropdown ? styles.customSelectActive : ''}`}
                  onClick={() => setShowMediaDropdown(!showMediaDropdown)}
                >
                  <span className={styles.customSelectValue}>
                    {mediaTypes.find(m => m.id === mediaId)?.name || t("SELECT_MEDIA")}
                  </span>
                  <span className={styles.customSelectArrow}>▼</span>
                </div>
                {showMediaDropdown && (
                  <div className={styles.customSelectDropdown}>
                    <div className={styles.dropdownOptions}>
                      {mediaTypes.map((media) => (
                        <div
                          key={media.id}
                          className={`${styles.dropdownOption} ${mediaId === media.id ? styles.dropdownOptionSelected : ''}`}
                          onClick={() => {
                            setMediaId(media.id);
                            setShowMediaDropdown(false);
                          }}
                        >
                          <span className={styles.optionText}>{media.name}</span>
                          {mediaId === media.id && <span className={styles.checkmark}>✓</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : null}


          <div className={styles.formGroup}>
            <label className={styles.label}>
              <span className={styles.labelIcon}>📝</span>
              {isOther ? (t("MESSAGE_CONTENT") || "תוכן ההודעה") : t("APPOINTMENTS_NOTES")}
            </label>
            <textarea
              className={styles.textarea}
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              rows={3}
              placeholder={t("APPOINTMENTS_NOTES_PLACEHOLDER")}
            />
          </div>

          {/* Warnings Section */}
          {conflicts.length > 0 && (
            <div className={styles.warningsSection}>
              <div className={styles.warningsTitle}>
                <span>⚠️</span>
                {t("APPOINTMENTS_WARNINGS")}
              </div>
              <div className={styles.warningsList}>
                {conflicts.map((conflict, index) => (
                  <div
                    key={index}
                    className={`${styles.warningItem} ${
                      conflict.severity === 'warning' ? styles.warningHigh : styles.warningLow
                    }`}
                  >
                    <span className={styles.warningIcon}>
                      {conflict.severity === 'warning' ? '⚠️' : 'ℹ️'}
                    </span>
                    <span>{conflict.message}</span>
                    {conflict.type === 'therapist_not_available' && therapistAvailability.length > 0 && (
                      <div className={styles.availabilityTooltip}>
                        <span className={styles.availabilityLabel}>
                          📅 {t("THERAPIST_AVAILABILITY")}:
                        </span>
                        <div className={styles.availabilityList}>
                          {therapistAvailability.map((av, idx) => (
                            <div key={idx} className={styles.availabilityItem}>
                              {getDayName(av.dayId)}: {av.startTime} - {av.endTime}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className={styles.warningNote}>
                {t("APPOINTMENTS_CAN_OVERRIDE")}
              </div>
            </div>
          )}
          {saveError && (
            <div
              className={styles.warningsSection}
              style={{ borderColor: "#ef4444", backgroundColor: "#fef2f2" }}
            >
              <div className={styles.warningItem}>
                <span className={styles.warningIcon}>⚠️</span>
                <span>{saveError}</span>
              </div>
            </div>
          )}
      </div>

      <div className={styles.modalFooter}>
        <button className={styles.cancelBtn} onClick={onClose}>
          {t("APPOINTMENTS_CLOSE")}
        </button>
        <button
          className={styles.saveBtn}
          onClick={handleSave}
          disabled={isPatchingAppointment || !isStartBeforeEnd || isValidating}
        >
          {isPatchingAppointment ? t("SAVING") : t("APPOINTMENTS_SAVE")}
        </button>
      </div>
    </>
  );

  // The same picker the creation screen uses, so the rules a user learned there hold here too:
  // the modality comes from the protocol and only free machines are offered.
  const devicePicker = showDevicePicker && companyDevices.length > 0 && (
    <DeviceConfigurationModal
      devices={companyDevices}
      initialDevice={currentDevice}
      protocolTemplate={currentDevice}
      protocolRequired={false}
      onConfirm={(device) => {
        setCurrentDevice(device);
        setShowDevicePicker(false);
      }}
      onCancel={() => setShowDevicePicker(false)}
    />
  );

  if (isEmbedded) {
    return (
      <div className={styles.embeddedWrapper} dir={isRtl ? "rtl" : "ltr"}>
        {editFormContent}
        {devicePicker}
      </div>
    );
  }

  return (
    <div
      className={styles.modalOverlay}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className={styles.modal} dir={isRtl ? "rtl" : "ltr"}>
        {editFormContent}
      </div>
      {devicePicker}
    </div>
  );
};

export default EditAppointmentModal;
